/**
 * LA MARQUE ORGAZONE — une seule gravure pour toutes les surfaces web.
 *
 * ⚠️ Elle vivait en TROIS copies (Console, Store, favicon du Store) et l'IdP en servait une
 * QUATRIÈME, différente : deux gravures du même dessin circulaient sans que rien ne le signale.
 * Le fichier est désormais ici, importé par les apps — une marque n'a qu'une source, comme un jeton.
 *
 * ⚠️ La source amont reste `assets/logos/` à la racine du dépôt (ce que le designer livre). Ce
 * fichier-ci en est la copie EXPLOITABLE par les bundlers web : Vite ne suit pas un import hors
 * racine de projet, d'où le passage par un paquet du workspace plutôt qu'un chemin relatif.
 */
import orgazoneMarkUrl from './assets/orgazone-mark.png';
import sliderzMarkUrl from './assets/sliderz-mark.png';

export { orgazoneMarkUrl, sliderzMarkUrl };

/**
 * La marque, prête à poser dans un rail ou une carte.
 *
 * `objectFit: contain` et non un `background-image` : le dessin garde ses proportions quelle que
 * soit la boîte, et reste visible sur les deux thèmes (son fond est transparent).
 */
export function OrgazoneMark({ size = 26 }: { size?: number }) {
  return (
    <img
      src={orgazoneMarkUrl}
      width={size}
      height={size}
      alt=""
      aria-hidden
      style={{ display: 'block', objectFit: 'contain', flexShrink: 0 }}
    />
  );
}

/**
 * LA MARQUE SLIDERZ — le monogramme du produit de caisse, côté WEB.
 *
 * ⚠️ POURQUOI ICI : la gravure n'avait pas de domicile web. Elle vivait dans `pos-shell`, un package
 * **React Native** qu'une SPA ne peut pas importer, et en copies dans `apps/store/design`. Le
 * back-office l'affiche dans son rail : sans ce domicile, il en aurait fait une copie de plus — et
 * c'est ainsi qu'on se retrouve avec deux gravures du même dessin, comme la marque Orgazone l'a été.
 * Fichier identique OCTET POUR OCTET à la livraison du designer (`assets/logos/`) et à la copie de
 * `pos-shell` — gardé par `apps/sliderz/retail/__tests__/marque-monogramme.test.tsx`.
 *
 * ⚠️ LE PRODUIT ET L'ÉDITEUR NE SE CONFONDENT PAS, et la ligne de partage est le DESTINATAIRE de la
 * surface, pas sa nature technique : `SliderzMark` signe le produit de caisse, son back-office et la
 * console EDI — qui alimente les catalogues du commerçant et appartient donc à la chaîne du produit ;
 * `OrgazoneMark` signe les surfaces de l'ÉDITEUR (Console, store privé, IdP).
 *
 * ⚠️ L'EDI A CHANGÉ DE CÔTÉ, PAR ARBITRAGE DE L'UTILISATEUR, et c'est pour ça que la règle est réécrite
 * ici plutôt que contournée dans l'app : la maquette posait le monogramme Sliderz pendant que cette
 * phrase nommait l'EDI du côté Orgazone. Deux sources de vérité opposées, chacune cohérente avec
 * elle-même — le genre d'écart qu'on ne tranche pas en silence dans un seul des deux fichiers.
 */
/**
 * ⚠️ `height` SÉPARÉ DE `size` : la maquette du back-office pose le monogramme en 28×29, pas en carré.
 * Sans ce paramètre, l'app aurait posé sa propre `<img>` à côté de la primitive — et le jour où le
 * fichier change, une seule des deux le saurait.
 */
export function SliderzMark({ size = 26, height = size }: { size?: number; height?: number }) {
  return (
    <img
      src={sliderzMarkUrl}
      width={size}
      height={height}
      alt=""
      aria-hidden
      style={{ display: 'block', objectFit: 'contain', flexShrink: 0 }}
    />
  );
}
